import React from 'react';
import { Info, TrendingUp, Activity, Users, Zap, Clock } from 'lucide-react';
import { LikelihoodFactors } from '../../services/RiskScoreCalculator';

interface LikelihoodAssessmentProps {
  data: LikelihoodFactors;
  onChange: (data: LikelihoodFactors) => void;
  calculatedScore: number;
  confidence: number;
  reasoning: string;
}

export const LikelihoodAssessment: React.FC<LikelihoodAssessmentProps> = ({
  data,
  onChange,
  calculatedScore,
  confidence,
  reasoning
}) => {
  // Check if user has entered meaningful data
  const hasUserInput = data.transactionCount > 0 || data.reachScale > 0 || (data.historicalIncidents?.count || 0) > 0 || (data.dollarValue || 0) > 0;

  const getScoreLabel = (score: number): string => {
    const labels: { [key: number]: string } = {
      1: 'Very Low',
      2: 'Low',
      3: 'Medium',
      4: 'High',
      5: 'Very High'
    };
    return labels[score] || 'Medium';
  };

  const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
    <span className="inline-flex items-center ml-1 text-gray-400 hover:text-gray-600 cursor-help" title={text}>
      <Info className="w-4 h-4" />
    </span>
  );

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <TrendingUp className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Likelihood Assessment</h3>
          <p className="text-sm text-gray-600">Evaluate factors that influence the probability of this risk occurring</p>
        </div>
      </div>

      {/* Volume Period */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Volume Period
          <InfoTooltip text="Select the time period for measuring transaction volume" />
        </label>
        <div className="flex gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              checked={data.volumePeriod === 'monthly'}
              onChange={() => onChange({ ...data, volumePeriod: 'monthly' })}
              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm">Monthly</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              checked={data.volumePeriod === 'annual'}
              onChange={() => onChange({ ...data, volumePeriod: 'annual' })}
              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm">Annual</span>
          </label>
        </div>
      </div>

      {/* Transaction/Activity Count */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Activity className="w-4 h-4 inline mr-1" />
          Transaction/Activity Count
          <InfoTooltip text="Number of transactions or activities processed in the selected period" />
        </label>
        <input
          type="number"
          value={data.transactionCount || ''}
          onChange={(e) => onChange({ ...data, transactionCount: parseInt(e.target.value) || 0 })}
          placeholder="Enter count..."
          min="0"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">Higher volume typically indicates higher likelihood</p>
      </div>

      {/* Dollar Value Processed */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dollar Value Processed (Optional)
          <InfoTooltip text="Total monetary value processed - provides additional context" />
        </label>
        <div className="relative">
          <span className="absolute left-3 top-2.5 text-gray-500">$</span>
          <input
            type="number"
            value={data.dollarValue || ''}
            onChange={(e) => onChange({ ...data, dollarValue: parseFloat(e.target.value) || undefined })}
            placeholder="Enter amount..."
            min="0"
            step="0.01"
            className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Reach/Scale (Entities Affected) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Users className="w-4 h-4 inline mr-1" />
          Reach/Scale (Entities Affected)
          <InfoTooltip text="Number of customers, accounts, or entities that could be affected by this risk" />
        </label>
        <input
          type="number"
          value={data.reachScale || ''}
          onChange={(e) => onChange({ ...data, reachScale: parseInt(e.target.value) || 0 })}
          placeholder="Enter number..."
          min="0"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">Number of customers, accounts, or stakeholders impacted</p>
      </div>

      {/* Automation Level */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Zap className="w-4 h-4 inline mr-1" />
          Automation Level
          <InfoTooltip text="Manual processes have higher likelihood of errors than automated ones" />
        </label>
        <select
          value={data.automationLevel || 'semi-automated'}
          onChange={(e) => onChange({ ...data, automationLevel: e.target.value as any })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="fully-automated">Fully Automated (Lowest Risk)</option>
          <option value="semi-automated">Semi-Automated (Medium Risk)</option>
          <option value="manual">Manual (Highest Risk)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Manual: 5/5 risk | Semi-Automated: 3/5 risk | Fully Automated: 1/5 risk
        </p>
      </div>

      {/* Historical Incidents */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Clock className="w-4 h-4 inline mr-1" />
          Historical Incidents
          <InfoTooltip text="Past incidents are strong predictors of future occurrences" />
        </label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <input
              type="number"
              value={data.historicalIncidents?.count || ''}
              onChange={(e) => onChange({
                ...data,
                historicalIncidents: {
                  ...data.historicalIncidents,
                  count: parseInt(e.target.value) || 0
                }
              })}
              placeholder="Count"
              min="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Number of incidents</p>
          </div>
          <div>
            <select
              value={data.historicalIncidents.timeframe || '12 months'}
              onChange={(e) => onChange({
                ...data,
                historicalIncidents: {
                  ...data.historicalIncidents,
                  timeframe: e.target.value
                }
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="6 months">in last 6 months</option>
              <option value="12 months">in last 12 months</option>
              <option value="24 months">in last 24 months</option>
              <option value="36 months">in last 36 months</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Time period</p>
          </div>
        </div>
      </div>

      {/* Calculated Score Display - Only show when user has entered data */}
      {hasUserInput ? (
        <div className="mt-8 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Calculated Likelihood Score</h4>
              <p className="text-sm text-gray-600">Based on weighted algorithm analysis</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-green-700">{calculatedScore}/5</div>
              <div className="text-sm text-gray-600 mt-1">{getScoreLabel(calculatedScore)}</div>
            </div>
          </div>

          {/* Confidence Indicator */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-700 font-medium">Data Confidence</span>
              <span className="text-gray-900 font-semibold">{(confidence * 100).toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  confidence >= 0.8 ? 'bg-green-500' :
                  confidence >= 0.6 ? 'bg-yellow-500' :
                  'bg-orange-500'
                }`}
                style={{ width: `${confidence * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Confidence level based on completeness of input data (8 factors)
            </p>
          </div>

          {/* AI Reasoning */}
          {reasoning && (
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                AI Reasoning
              </h5>
              <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                {reasoning}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
          <div className="text-center text-gray-600">
            <p className="text-sm font-medium">Fill in the likelihood factors above to see the calculated score</p>
          </div>
        </div>
      )}
    </div>
  );
};
